import { useCallback, useEffect, useRef, useState } from 'react';
import {
	buildPlatformLogoutUrl,
	clearAllKhalStorage,
	getActiveKernelToken,
} from '../../lib/platform-logout';
import { useWindowStore } from '../../stores/window-store';

/** Detect Tauri runtime. */
function isTauri(): boolean {
	return typeof window !== 'undefined' && '__TAURI__' in window;
}

const DEFAULT_PLATFORM_URL = 'https://platform.khal.ai';

function resolvePlatformUrl(): string {
	const env =
		typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env;
	const configured = env ? env.VITE_PLATFORM_API_URL : undefined;
	return typeof configured === 'string' && configured.length > 0 ? configured : DEFAULT_PLATFORM_URL;
}

/**
 * Items without a backend wiring (Scaffold, Connect to kernel, Lock screen)
 * emit a Sentry breadcrumb so we can see how often users click them while
 * the feature is still pending. Dynamic import keeps Sentry optional.
 */
function emitKMenuTodoBreadcrumb(item: string): void {
	import('@sentry/react')
		.then((Sentry) => {
			Sentry.addBreadcrumb({
				category: 'kmenu-todo',
				message: item,
				level: 'info',
			});
		})
		.catch(() => {
			// Sentry not available — ignore
		});
}

interface KMenuItem {
	id: 'about' | 'settings' | 'market' | 'scaffold' | 'kernel' | 'lock' | 'logout';
	label: string;
	kbd?: string;
}

interface KMenuSeparator {
	id: 'sep';
}

type KMenuRow = KMenuItem | KMenuSeparator;

function isSeparator(row: KMenuRow): row is KMenuSeparator {
	return row.id === 'sep';
}

/** Designer's actual KMenu list (khalos-shell.jsx:248-258). */
const ITEMS: KMenuRow[] = [
	{ id: 'about', label: 'About KhalOS' },
	{ id: 'settings', label: 'Settings…', kbd: '⌘,' },
	{ id: 'market', label: 'Marketplace' },
	{ id: 'sep' },
	{ id: 'scaffold', label: 'Scaffold pack…', kbd: '⌥⌘N' },
	{ id: 'kernel', label: 'Connect to kernel…' },
	{ id: 'sep' },
	{ id: 'lock', label: 'Lock screen', kbd: '⌃⌘L' },
	{ id: 'logout', label: 'Sign out' },
];

interface KMenuProps {
	open: boolean;
	onClose: () => void;
}

export function KMenu({ open, onClose }: KMenuProps) {
	const menuRef = useRef<HTMLDivElement>(null);
	const [appVersion, setAppVersion] = useState('dev');

	useEffect(() => {
		if (isTauri()) {
			import('@tauri-apps/api/app').then(({ getVersion }) => {
				getVersion().then(setAppVersion).catch(() => {});
			});
		}
	}, []);

	/* Close on Escape */
	useEffect(() => {
		if (!open) return;
		const handler = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.preventDefault();
				onClose();
			}
		};
		window.addEventListener('keydown', handler, { capture: true });
		return () => window.removeEventListener('keydown', handler, { capture: true });
	}, [open, onClose]);

	/* Close on click outside */
	useEffect(() => {
		if (!open) return;
		const handler = (e: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
				onClose();
			}
		};
		const timer = setTimeout(() => {
			window.addEventListener('mousedown', handler);
		}, 0);
		return () => {
			clearTimeout(timer);
			window.removeEventListener('mousedown', handler);
		};
	}, [open, onClose]);

	const handleAbout = useCallback(() => {
		// Khal OS has no dedicated About modal yet; show the version inline.
		// biome-ignore lint/suspicious/noAlert: Apple-style "About this Mac" affordance, no modal yet.
		window.alert(`Khal OS v${appVersion}`);
		onClose();
	}, [appVersion, onClose]);

	const handleSettings = useCallback(() => {
		useWindowStore.getState().openWindow({ title: 'Settings', appId: 'settings' });
		onClose();
	}, [onClose]);

	const handleMarketplace = useCallback(() => {
		useWindowStore.getState().openWindow({ title: 'App Store', appId: 'marketplace' });
		onClose();
	}, [onClose]);

	const handleSignOut = useCallback(() => {
		if (isTauri()) {
			// biome-ignore lint/suspicious/noExplicitAny: Tauri global is untyped
			(window as any).__TAURI__?.core?.invoke('switch_account').catch(console.error);
			onClose();
			return;
		}
		// PWA: mirror UserMenu's logout flow so KMenu sign-out matches the avatar dropdown.
		const token = getActiveKernelToken();
		clearAllKhalStorage();
		const logoutUrl = buildPlatformLogoutUrl({
			platformUrl: resolvePlatformUrl(),
			pwaOrigin: window.location.origin,
			token,
		});
		window.location.assign(logoutUrl);
	}, [onClose]);

	// TODO(scaffold): wire to pack-scaffold flow once shipped.
	const handleScaffold = useCallback(() => {
		emitKMenuTodoBreadcrumb('scaffold');
		// biome-ignore lint/suspicious/noConsole: explicit TODO surface for unwired KMenu item
		console.warn('[kmenu] TODO: Scaffold pack…');
		onClose();
	}, [onClose]);

	// TODO(kernel): wire to kernel-connect overlay once AuthOverlay (G8) lands.
	const handleConnectKernel = useCallback(() => {
		emitKMenuTodoBreadcrumb('kernel');
		// biome-ignore lint/suspicious/noConsole: explicit TODO surface for unwired KMenu item
		console.warn('[kmenu] TODO: Connect to kernel…');
		onClose();
	}, [onClose]);

	// TODO(lock): wire to lock-screen overlay once screen-lock state is in place.
	const handleLock = useCallback(() => {
		emitKMenuTodoBreadcrumb('lock');
		// biome-ignore lint/suspicious/noConsole: explicit TODO surface for unwired KMenu item
		console.warn('[kmenu] TODO: Lock screen');
		onClose();
	}, [onClose]);

	const dispatch = useCallback(
		(id: string) => {
			switch (id) {
				case 'about':
					handleAbout();
					return;
				case 'settings':
					handleSettings();
					return;
				case 'market':
					handleMarketplace();
					return;
				case 'scaffold':
					handleScaffold();
					return;
				case 'kernel':
					handleConnectKernel();
					return;
				case 'lock':
					handleLock();
					return;
				case 'logout':
					handleSignOut();
					return;
			}
		},
		[
			handleAbout,
			handleSettings,
			handleMarketplace,
			handleScaffold,
			handleConnectKernel,
			handleLock,
			handleSignOut,
		]
	);

	if (!open) return null;

	return (
		<div
			ref={menuRef}
			className="k-menu k-popover"
			style={{
				position: 'fixed',
				zIndex: 9600,
				top: 38,
				right: 8,
				width: 240,
				padding: 4,
			}}
		>
			<div className="k-menu-h">{`khalos · ${appVersion}`}</div>
			{ITEMS.map((row, i) => {
				if (isSeparator(row)) {
					// biome-ignore lint/suspicious/noArrayIndexKey: stable static list
					return <div key={`sep-${i}`} className="k-menu-sep k-divider" />;
				}
				return (
					<button
						type="button"
						key={row.id}
						className="k-menu-item"
						style={{
							width: '100%',
							background: 'transparent',
							border: 'none',
							font: 'inherit',
							cursor: 'default',
							textAlign: 'left',
						}}
						onClick={() => dispatch(row.id)}
					>
						<span className="k-grow">{row.label}</span>
						{row.kbd && <span className="k-kbd">{row.kbd}</span>}
					</button>
				);
			})}
		</div>
	);
}
